import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Auth } from '@aws-amplify/auth';
import { GetDataEndpointCommand, KinesisVideoClient, ListStreamsCommand } from '@aws-sdk/client-kinesis-video';
import { HLSFragmentSelectorType, HLSPlaybackMode, KinesisVideoArchivedMediaClient } from '@aws-sdk/client-kinesis-video-archived-media';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Alert, MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Environment } from 'internal-config';
import React, { useContext, useEffect, useReducer } from 'react';
import { useModularTimePeriodSelector } from '../../../../../hooks/useModularTimePeriodSelector';
import { DefaultReducer, defaultState, KinesisVideoStreamContext } from '../../../../VideoStream/Context/KinesisVideoStreamContext';
import { AWS_KVS_Player, HLS_Player } from '../../../../VideoStream/common/player';
import { usePlayerState } from '../../../../VideoStream/common/usePlayerState';
import { useFragmentTable } from '../../../../VideoStream/common/useFragmentTable';
const { projectCode, stage } = Environment;
const shortProjectCode = projectCode.substring(0, 4);
//https://www.npmjs.com/package/@aws-sdk/client-kinesis-video-archived-media
const cloudfrontDistributionUrl = `https://video.${stage}.${shortProjectCode}.iotv.link`;
let protocol = 'NOT_DASH';
const { region } = Environment;
const tempBaseDate = dayjs().subtract(3, 'minutes');
const maxToTimestamp = Math.min(Date.now(), tempBaseDate.valueOf() + Milliseconds.DAY);
export const VideoTab = () => {
    const [state, dispatch] = useReducer(DefaultReducer, { ...defaultState, selectedHLSPlaybackMode: HLSPlaybackMode.ON_DEMAND });
    return (_jsx(KinesisVideoStreamContext.Provider, { value: { state, dispatch }, children: _jsx(VideoTabInternal, {}) }));
};
export const VideoTabInternal = () => {
    const { fromDateValue, toDateValue, components: { TimePeriodSelector }, } = useModularTimePeriodSelector({ rangeLength: 1, rangeUnit: 'days', initialToTime: dayjs(maxToTimestamp) });
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const { kvClient, kvaClient, selectedStreamInfo, StreamInfoList, DataEndpoint, HLSStreamingSessionURL, selectedHLSPlaybackMode, selectedHLSFragmentSelectorType, fetchVideoStreamError, Fragments, } = state;
    const setKVClient = async () => {
        const credentials = await Auth.currentUserCredentials();
        const kvClient = new KinesisVideoClient({ region, credentials });
        dispatch({ kvClient });
    };
    const fetchDataEndpoint = async () => {
        if (kvClient && selectedStreamInfo) {
            console.log('Fetching data endpoint');
            try {
                const { DataEndpoint } = await kvClient.send(new GetDataEndpointCommand({
                    StreamARN: selectedStreamInfo.StreamARN,
                    APIName: protocol === 'DASH' ? 'GET_DASH_STREAMING_SESSION_URL' : 'GET_HLS_STREAMING_SESSION_URL',
                }));
                console.log(`Data Endpoint ${DataEndpoint}`);
                const credentials = await Auth.currentUserCredentials();
                const kvaClient = new KinesisVideoArchivedMediaClient({ region, credentials, endpoint: DataEndpoint });
                dispatch({ DataEndpoint, kvaClient });
            }
            catch (e) {
                console.error(`Fetch Endpoint ${e.message}`);
            }
        }
    };
    const listStreams = async () => {
        if (kvClient) {
            const params = {
                NextToken: undefined,
                StreamNameCondition: { ComparisonOperator: 'BEGINS_WITH', ComparisonValue: shortProjectCode },
            };
            const command = new ListStreamsCommand(params);
            try {
                const { StreamInfoList = null, NextToken } = await kvClient.send(command);
                console.log('ListStreams Res', StreamInfoList);
                const newSelectedStreamInfo = selectedStreamInfo ?? StreamInfoList?.at(0) ?? null;
                dispatch({ selectedStreamInfo: newSelectedStreamInfo, StreamInfoList });
            }
            catch (e) {
                console.error(`ListStreams ${e.message}`);
            }
        }
    };
    const fromTimestamp = fromDateValue.unix() * Milliseconds.SECOND;
    const toTimestamp = toDateValue.unix() * Milliseconds.SECOND;
    const PlaybackMode = selectedHLSPlaybackMode;
    const TimestampRange = PlaybackMode === HLSPlaybackMode.LIVE
        ? undefined
        : {
            StartTimestamp: new Date(fromTimestamp),
            EndTimestamp: new Date(toTimestamp),
        };
    const DisplayFragmentTimestamp = 'ALWAYS';
    const FragmentSelectorType = selectedHLSFragmentSelectorType;
    const ContainerFormat = 'MPEG_TS';
    const DiscontinuityMode = 'ALWAYS';
    const MaxMediaPlaylistFragmentResults = 1000;
    const { isLive, fragmentTable } = useFragmentTable({ selectedStreamInfo, kvaClient, fromTimestamp, toTimestamp, FragmentSelectorType });
    useEffect(() => {
        setKVClient();
    }, []);
    useEffect(() => {
        if (kvClient) {
            listStreams();
        }
    }, [kvClient]);
    useEffect(() => {
        if (selectedStreamInfo) {
            fetchDataEndpoint();
        }
    }, [selectedStreamInfo]);
    useEffect(() => {
        dispatch({ fromTimestamp, toTimestamp });
    }, [toTimestamp, fromTimestamp,]);
    const { playerState, playerEventFns } = usePlayerState({});
    const modes = [HLSPlaybackMode.LIVE, HLSPlaybackMode.LIVE_REPLAY, HLSPlaybackMode.ON_DEMAND];
    const modeSelector = (_jsx(Select, { sx: { width: '10rem' },
        size: 'small',
        value: selectedHLSPlaybackMode,
        select: true,
        children: modes.map((mode) => (_jsx(MenuItem, { value: mode, children: mode }, mode))),
        onChange: (e) => e.target.value && dispatch({ selectedHLSPlaybackMode: e.target.value }) }));
    const fragmentTypes = [HLSFragmentSelectorType.PRODUCER_TIMESTAMP, HLSFragmentSelectorType.SERVER_TIMESTAMP];
    const fragmentTypeSelector = (_jsx(Select, { sx: { width: '10rem' },
        size: 'small',
        value: selectedHLSFragmentSelectorType,
        select: true,
        children: fragmentTypes.map((type) => (_jsx(MenuItem, { value: type, children: type }, type))),
        onChange: (e) => e.target.value && dispatch({ selectedHLSFragmentSelectorType: e.target.value }) }));
    const streamSelector = StreamInfoList ? (_jsx(Select, { sx: { width: '20rem' },
        size: 'small',
        value: selectedStreamInfo?.StreamARN ?? '',
        select: true,
        placeholder: 'select stream',
        children: StreamInfoList.map(({ StreamName, StreamARN }) => (_jsx(MenuItem, { value: StreamARN, children: StreamName }, StreamARN))),
        onChange: (e) => dispatch({ selectedStreamInfo: StreamInfoList.find((si) => si.StreamARN === e.target.value) }) })) : (_jsx(Typography, { children: "No Streams Found" }));
    const fragmentSummary = Fragments ? (_jsx(Stack, { sx: { height: 400 }, children: fragmentTable })) : null;
    return (_jsxs(Stack, { children: [_jsxs(Stack, { direction: 'row', children: [streamSelector, modeSelector, fragmentTypeSelector] }), selectedHLSPlaybackMode !== HLSPlaybackMode.LIVE && _jsx(Stack, { direction: 'row', children: TimePeriodSelector }), DataEndpoint && HLSStreamingSessionURL ? (_jsx(AWS_KVS_Player, { playerEventFns, PlayerComponent: HLS_Player })) : fetchVideoStreamError ? (_jsx(Alert, { severity: 'warning', children: fetchVideoStreamError })) : (_jsx(Skeleton, { height: 600 })), fragmentSummary] }));
};
